import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Send, 
  Download, 
  Plus,
  Bot,
  User,
  FileText,
  ExternalLink,
  Loader2,
  Sparkles,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Arbres",
  "Arbustes",
  "Plantes",
  "Gazon",
  "Béton",
  "Pierres",
  "Clôtures",
  "Mobilier",
  "Éclairage",
  "Arrosage",
  "Terrassement",
  "Main d'œuvre",
  "Autre"
];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const chatEndRef = useRef(null);
  
  const [project, setProject] = useState(null);
  const [items, setItems] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [selectedModel, setSelectedModel] = useState("claude");
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [newItem, setNewItem] = useState({
    category: "",
    description: "",
    reference: "",
    quantity: 1,
    unit: "pièce",
    unit_price: 0
  });

  const fetchData = async () => {
    try {
      const [projectRes, itemsRes, chatRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/quote-items/${id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/chat/${id}`, { headers: getAuthHeaders() })
      ]);
      setProject(projectRes.data);
      setItems(itemsRes.data);
      setMessages(chatRes.data.messages || []);
    } catch (error) {
      toast.error("Erreur lors du chargement");
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput("");
    setChatLoading(true);

    // Add user message immediately
    setMessages(prev => [...prev, {
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);

    try {
      const response = await axios.post(
        `${API}/chat`,
        {
          project_id: id,
          message: userMessage,
          model: selectedModel
        },
        { headers: getAuthHeaders() }
      );

      setMessages(prev => [...prev, {
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString(),
        sources: response.data.sources,
        model: response.data.model
      }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de communication");
    } finally {
      setChatLoading(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(
        `${API}/quote-items`,
        { ...newItem, project_id: id },
        { headers: getAuthHeaders() }
      );
      setItems(prev => [...prev, response.data]);
      setAddItemOpen(false);
      setNewItem({
        category: "",
        description: "",
        reference: "",
        quantity: 1,
        unit: "pièce",
        unit_price: 0
      });
      toast.success("Élément ajouté");
      fetchData(); // Refresh project totals
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/quote-items/${itemId}`, { headers: getAuthHeaders() });
      setItems(prev => prev.filter(i => i.id !== itemId));
      toast.success("Élément supprimé");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(
        `${API}/projects/${id}/export/excel`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `devis_${project?.name?.replace(/\s+/g, '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export téléchargé");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CH', { style: 'currency', currency: 'CHF' }).format(amount);
  };

  const groupedItems = items.reduce((acc, item) => {
    const cat = item.category || "Autre";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1E8B5A]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
              {project?.name}
            </h1>
            <p className="text-gray-500 mt-1">
              {project?.client_name || "Sans client"} • {items.length} éléments • {formatCurrency(project?.total_amount || 0)}
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleExport} className="gap-2" data-testid="export-btn">
              <Download className="h-4 w-4" />
              Exporter Excel
            </Button>
            <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary gap-2" data-testid="add-item-btn">
                  <Plus className="h-4 w-4" />
                  Ajouter élément
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Ajouter un élément</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddItem} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Select 
                      value={newItem.category}
                      onValueChange={(val) => setNewItem({ ...newItem, category: val })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      value={newItem.description}
                      onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                      placeholder="Ex: Chêne pédonculé 300/400"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Référence</Label>
                    <Input
                      value={newItem.reference}
                      onChange={(e) => setNewItem({ ...newItem, reference: e.target.value })}
                      placeholder="Ex: REF-001"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Quantité</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newItem.quantity}
                        onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 0 })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unité</Label>
                      <Select 
                        value={newItem.unit}
                        onValueChange={(val) => setNewItem({ ...newItem, unit: val })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pièce">Pièce</SelectItem>
                          <SelectItem value="m²">m²</SelectItem>
                          <SelectItem value="m³">m³</SelectItem>
                          <SelectItem value="ml">ml</SelectItem>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="heure">Heure</SelectItem>
                          <SelectItem value="jour">Jour</SelectItem>
                          <SelectItem value="forfait">Forfait</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Prix unitaire</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newItem.unit_price}
                        onChange={(e) => setNewItem({ ...newItem, unit_price: parseFloat(e.target.value) || 0 })}
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setAddItemOpen(false)}>
                      Annuler
                    </Button>
                    <Button type="submit" className="btn-primary">
                      Ajouter
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Main content */}
        <Tabs defaultValue="chat" className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="chat" className="gap-2">
              <Sparkles className="h-4 w-4" />
              Assistant
            </TabsTrigger>
            <TabsTrigger value="items" className="gap-2">
              <FileText className="h-4 w-4" />
              Éléments ({items.length})
            </TabsTrigger>
            <TabsTrigger value="synthesis" className="gap-2">
              Synthèse
            </TabsTrigger>
          </TabsList>

          {/* Chat Tab */}
          <TabsContent value="chat">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2">
                <CardHeader className="border-b">
                  <div className="flex items-center justify-between">
                    <CardTitle>Assistant de chiffrage</CardTitle>
                    <Select value={selectedModel} onValueChange={setSelectedModel}>
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="claude">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-purple-500" />
                            Modèle avancé
                          </span>
                        </SelectItem>
                        <SelectItem value="gpt">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            Modèle standard
                          </span>
                        </SelectItem>
                        <SelectItem value="gemini">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500" />
                            Modèle rapide
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px] p-4">
                    {messages.length === 0 ? (
                      <div className="h-full flex flex-col items-center justify-center text-center p-8">
                        <div className="h-16 w-16 rounded-2xl bg-green-50 flex items-center justify-center mb-4">
                          <Bot className="h-8 w-8 text-[#1E8B5A]" />
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-2">
                          Assistant de chiffrage
                        </h3>
                        <p className="text-sm text-gray-500 max-w-md">
                          Posez vos questions pour obtenir des chiffrages basés sur votre base de connaissances 
                          ou des recherches web.
                        </p>
                        <div className="mt-6 space-y-2">
                          <p className="text-xs text-gray-400">Suggestions :</p>
                          <div className="flex flex-wrap gap-2 justify-center">
                            {[
                              "Quel est le prix d'un chêne pédonculé 300/400 ?",
                              "Chiffre-moi 50m² de pavés granit",
                              "Prix moyen clôture bois 2m hauteur"
                            ].map((suggestion, idx) => (
                              <button
                                key={idx}
                                onClick={() => setChatInput(suggestion)}
                                className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {messages.map((msg, idx) => (
                          <div
                            key={idx}
                            className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                          >
                            <div className={`
                              h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0
                              ${msg.role === 'user' ? 'bg-gray-200' : 'bg-green-100'}
                            `}>
                              {msg.role === 'user' ? (
                                <User className="h-4 w-4 text-gray-600" />
                              ) : (
                                <Bot className="h-4 w-4 text-[#1E8B5A]" />
                              )}
                            </div>
                            <div className={`
                              flex-1 max-w-[80%] rounded-2xl px-4 py-3
                              ${msg.role === 'user' 
                                ? 'bg-[#1E8B5A] text-white ml-auto' 
                                : 'bg-gray-100 text-gray-900'}
                            `}>
                              <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                              {msg.sources?.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-200/50">
                                  <p className="text-xs opacity-70 mb-2">Sources :</p>
                                  <div className="flex flex-wrap gap-2">
                                    {msg.sources.map((source, sidx) => (
                                      <span 
                                        key={sidx}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-white/20 rounded text-xs"
                                      >
                                        <FileText className="h-3 w-3" />
                                        {source.filename}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        {chatLoading && (
                          <div className="flex gap-3">
                            <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                              <Bot className="h-4 w-4 text-[#1E8B5A]" />
                            </div>
                            <div className="bg-gray-100 rounded-2xl px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin text-[#1E8B5A]" />
                                <span className="text-sm text-gray-500">Réflexion en cours...</span>
                              </div>
                            </div>
                          </div>
                        )}
                        <div ref={chatEndRef} />
                      </div>
                    )}
                  </ScrollArea>
                  <div className="p-4 border-t">
                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <Input
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        placeholder="Posez votre question..."
                        className="flex-1"
                        disabled={chatLoading}
                        data-testid="chat-input"
                      />
                      <Button 
                        type="submit" 
                        className="btn-primary"
                        disabled={!chatInput.trim() || chatLoading}
                        data-testid="send-btn"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </form>
                  </div>
                </CardContent>
              </Card>

              {/* Quick add panel */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Résumé</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 rounded-xl bg-green-50">
                    <p className="text-sm text-gray-600">Total projet</p>
                    <p className="text-2xl font-bold text-[#1E8B5A]">
                      {formatCurrency(project?.total_amount || 0)}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">Par catégorie</p>
                    {Object.entries(groupedItems).map(([cat, catItems]) => {
                      const catTotal = catItems.reduce((sum, i) => sum + i.total_price, 0);
                      return (
                        <div key={cat} className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">{cat}</span>
                          <span className="font-medium">{formatCurrency(catTotal)}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Items Tab */}
          <TabsContent value="items">
            <Card>
              <CardContent className="p-0">
                {items.length === 0 ? (
                  <div className="p-12 text-center">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="font-medium text-gray-900 mb-2">Aucun élément</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Utilisez l'assistant ou ajoutez manuellement des éléments
                    </p>
                    <Button onClick={() => setAddItemOpen(true)} className="btn-primary">
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter un élément
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Catégorie</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Réf.</TableHead>
                        <TableHead className="text-right">Qté</TableHead>
                        <TableHead>Unité</TableHead>
                        <TableHead className="text-right">P.U.</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <span className="px-2 py-1 rounded-full text-xs bg-gray-100">
                              {item.category}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">{item.description}</TableCell>
                          <TableCell className="text-gray-500">{item.reference}</TableCell>
                          <TableCell className="text-right">{item.quantity}</TableCell>
                          <TableCell>{item.unit}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(item.total_price)}
                          </TableCell>
                          <TableCell>
                            {item.source_url ? (
                              <a 
                                href={item.source_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#1E8B5A] hover:underline flex items-center gap-1"
                              >
                                {item.source}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            ) : (
                              <span className="text-gray-400">{item.source}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteItem(item.id)}
                              className="h-8 w-8 text-gray-400 hover:text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Synthesis Tab */}
          <TabsContent value="synthesis">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {Object.entries(groupedItems).map(([cat, catItems]) => {
                const catTotal = catItems.reduce((sum, i) => sum + i.total_price, 0);
                return (
                  <Card key={cat}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{cat}</CardTitle>
                        <span className="text-lg font-bold text-[#1E8B5A]">
                          {formatCurrency(catTotal)}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {catItems.map((item) => (
                          <div 
                            key={item.id}
                            className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                          >
                            <div>
                              <p className="font-medium text-sm">{item.description}</p>
                              <p className="text-xs text-gray-500">
                                {item.quantity} {item.unit} × {formatCurrency(item.unit_price)}
                              </p>
                            </div>
                            <span className="font-semibold text-sm">
                              {formatCurrency(item.total_price)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {Object.keys(groupedItems).length === 0 && (
                <Card className="lg:col-span-2">
                  <CardContent className="p-12 text-center">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucun élément à synthétiser</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
